import { useQuery, queryOptions } from '@tanstack/react-query';
import { graphqlRequest, hasGraphQLNetworkError } from '~/adapters/graphqlRequest';
import { GetSurveyParticipantsDocument, SurveyParticipants } from '~/gql/graphql';
import {
  FetchAttributeResponsesQuery,
  FetchAttributeReportQuery,
  OverallSurveyResultsQuery,
  reportingQueries,
  ReportingQuery,
} from '../queries/reporting.queries';
import { appQueryClient } from '~/adapters/reactQuery';

export const reportingKeys = {
  getSurveyParticipants: ({ surveyId }) => ['getSurveyParticipants', { surveyId }],
  fetchBaseReport: ({ query }) => ['fetchBaseReport', { query }],
  findSurveys: ({ ids }) => ['findSurveys', { ids }],
  fetchAttributeResponses: ({ query }) => ['fetchAttributeResponses', { query }],
  overallSurveyResults: ({ query }) => ['overallSurveyResults', { query }],
  fetchAttributeReport: ({ query }) => ['fetchAttributeReport', { query }],
};

export const useGetSurveyParticipantsQuery = ({ surveyId, isOpen }) => {
  return useQuery({
    queryKey: reportingKeys.getSurveyParticipants({ surveyId }),
    queryFn: async () => {
      const res = await graphqlRequest<any>(GetSurveyParticipantsDocument, { surveyId });

      if (hasGraphQLNetworkError(res)) return Promise.reject(res.error);

      const queryParticipants: SurveyParticipants[] = res.data?.getSurveyParticipants;

      if (!queryParticipants?.length) return [];

      return queryParticipants.map((participant) => {
        // Removes undefined, null, and whitespaces from the name
        let title = [participant.firstName, participant.lastName].filter(Boolean).join(' ').trim();

        //  title can be either firstName + lastName, or email, or the employee id, in that order
        if (!title && participant.email) {
          title = participant.email;
        } else if (!title) {
          title = participant.employeeId;
        }

        return {
          id: participant.employeeSurveySnapshotId,
          title,
          invited: participant.invitedDate,
          submitted: participant.submittedResponseDate,
        };
      });
    },
    // 5 minutes of cache
    staleTime: 1000 * 60 * 5,
    enabled: isOpen,
  });
};

export const findSurveysOptions = (ids: number[], { skip = false }: { skip?: boolean } = {}) => {
  return queryOptions({
    queryKey: reportingKeys.findSurveys({ ids }),
    queryFn: () => reportingQueries.findSurveys(ids),
    staleTime: 0,
    enabled: !skip,
  });
};

export const useFindSurveysQuery = (ids: number[], { skip = false }: { skip?: boolean } = {}) => {
  return useQuery(findSurveysOptions(ids, { skip }));
};

export const fetchFindSurveysQuery = (ids: number[], { skip = false }: { skip?: boolean } = {}) => {
  return appQueryClient.fetchQuery(findSurveysOptions(ids, { skip }));
};

export const fetchAttributeResponsesOptions = (
  query: FetchAttributeResponsesQuery,
  { skip = false }: { skip?: boolean } = {},
) => {
  return queryOptions({
    queryKey: reportingKeys.fetchAttributeResponses({ query }),
    queryFn: () => reportingQueries.fetchAttributeResponses(query),
    staleTime: 0,
    enabled: !skip,
  });
};

export const useFetchAttributeResponsesQuery = (
  query: FetchAttributeResponsesQuery,
  { skip = false }: { skip?: boolean } = {},
) => {
  return useQuery(fetchAttributeResponsesOptions(query, { skip }));
};

export const fetchAttributeResponsesQuery = (query: FetchAttributeResponsesQuery) => {
  return appQueryClient.fetchQuery(fetchAttributeResponsesOptions(query));
};

export const fetchBaseReportQuery = async (query: ReportingQuery) => {
  return appQueryClient.fetchQuery({
    queryKey: reportingKeys.fetchBaseReport({ query }),
    queryFn: () => reportingQueries.fetchBaseReport(query),
    staleTime: 1000 * 60 * 5,
  });
};

export const overallSurveyResultsOptions = (query: OverallSurveyResultsQuery) => {
  return queryOptions({
    queryKey: reportingKeys.overallSurveyResults({ query }),
    queryFn: () => reportingQueries.overallSurveyResults(query),
    staleTime: 1000 * 60 * 5,
  });
};

export const useOverallSurveyResultsQuery = (query: OverallSurveyResultsQuery) => {
  return useQuery(overallSurveyResultsOptions(query));
};

export const fetchOverallSurveyResultsQuery = (query: OverallSurveyResultsQuery) => {
  return appQueryClient.fetchQuery(overallSurveyResultsOptions(query));
};

export const fetchAttributeReportOptions = (query: FetchAttributeReportQuery) => {
  return queryOptions({
    queryKey: reportingKeys.fetchAttributeReport({ query }),
    queryFn: () => reportingQueries.fetchAttributeReport(query),
    staleTime: 1000 * 60 * 5,
  });
};

export const fetchAttributeReportQuery = (query: FetchAttributeReportQuery) => {
  return appQueryClient.fetchQuery(fetchAttributeReportOptions(query));
};
